import { Navigate, useParams, useSearchParams } from 'react-router';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { usePermissions } from '@/entities/user';
import { ROUTES } from '@/shared/config/routes';
import { useContentStudioEnabled } from '@/shared/config/useContentStudioEnabled';

/**
 * Redirects to the first admin route the user has permission to access,
 * broadest destination first.
 *
 * Anyone `canViewStudioSettings` admits has to reach a page here — the
 * sidebar offers a single entry pointing at /admin, so a permission with no
 * branch shows a person the way in and then drops them on Home when they
 * take it.
 *
 * `mcp-catalog` needs no branch of its own: it is gated on `canManageTools`,
 * which already lands on the Tools page.
 */
export const AdminIndexRedirect = () => {
  const {
    canManageTools,
    canManageModelsWhitelisting,
    canManageUserAgents,
    canManageAdFormats,
    isInstanceAdmin,
    isWorkspaceAdmin,
  } = usePermissions();
  const connectedAppsEnabled = useBooleanFlagValue('connected_apps', false);
  const contentStudioEnabled = useContentStudioEnabled();

  if (canManageTools) {
    return <Navigate to={ROUTES.ADMIN_TOOLS} replace />;
  }
  if (canManageModelsWhitelisting) {
    return <Navigate to={ROUTES.ADMIN_MODELS} replace />;
  }
  if (isInstanceAdmin) {
    return <Navigate to={ROUTES.ADMIN_ETL_CONFIG} replace />;
  }
  if (isWorkspaceAdmin) {
    return <Navigate to={ROUTES.ADMIN_CUSTOMIZATION} replace />;
  }
  if (canManageAdFormats && contentStudioEnabled) {
    return <Navigate to={ROUTES.ADMIN_AD_FORMATS} replace />;
  }
  // The one settings page with no permission of its own, so the sidebar offers
  // /admin to everyone while its flag is on.
  if (connectedAppsEnabled) {
    return <Navigate to={ROUTES.ADMIN_CONNECTED_APPS} replace />;
  }
  // Last, and a form rather than a listing: it is the only page this key
  // opens, so it beats Home, but any settled page beats landing on a blank
  // creation form.
  if (canManageUserAgents) {
    return <Navigate to={ROUTES.ADMIN_CREATE_AGENT} replace />;
  }
  return <Navigate to={ROUTES.HOME} replace />;
};

/**
 * Redirects legacy /knowledge-bases/:id URLs to new /knowledge-hub/knowledge-bases/:id
 */
export const LegacyKBRedirect = () => {
  const { knowledgeBaseId } = useParams<{ knowledgeBaseId?: string }>();

  if (!knowledgeBaseId) {
    return <Navigate to={ROUTES.KNOWLEDGE_HUB_KB} replace />;
  }
  return <Navigate to={ROUTES.KNOWLEDGE_HUB_KB_DETAIL(knowledgeBaseId)} replace />;
};

/**
 * The creative harness now lives in each brand's kit: an old link opens that brand's harness
 * section, or the brand kits when it named no brand.
 */
export const LegacyReelsHarnessRedirect = () => {
  const [params] = useSearchParams();
  const brandId = params.get('brand');

  if (!brandId) {
    return <Navigate to={ROUTES.REELS_CREATOR_BRANDS} replace />;
  }
  return <Navigate to={ROUTES.REELS_CREATOR_BRAND_SECTION(brandId, 'harness')} replace />;
};
